/**
 * CallingDialer — the centerpiece of OneDial Africa.
 *
 * Modeled after Talk360 / Rebtel / Yolla:
 *   ┌──────────────────────────────┐
 *   │ Wallet £xx.xx · Rate £x/min   │   ← live status bar
 *   ├──────────────────────────────┤
 *   │   🇳🇬  Nigeria  +234       v   │   ← country chip (tap → picker)
 *   │     +234 801 234 5678          │   ← number display + backspace
 *   ├──────────────────────────────┤
 *   │  Recent · Mum · Dad · …        │   ← horizontal recents
 *   ├──────────────────────────────┤
 *   │  1   2   3                      │
 *   │  4   5   6      (large keypad)  │
 *   │  7   8   9                      │
 *   │  *   0   #                      │
 *   ├──────────────────────────────┤
 *   │      ●  G R E E N   C A L L     │
 *   └──────────────────────────────┘
 *
 * Behaviour:
 * - Logged-in: places a real call via /api/customer/calls/initiate, polls active call,
 *   ticks per-second cost, hangup, etc.
 * - Logged-out (public /dialer preview): keypad fully usable; pressing Call → /register
 *   with the number stashed so they can complete sign-up and place that same call.
 *
 * Onboarding 3-step strip only renders when balance == 0 AND no calls history.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Phone, PhoneOff, Delete, AlertCircle, Clock, ChevronDown,
  UserPlus, Sparkles, Loader2, Users, MessageSquare, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { walletApi, gbp } from "@/lib/wallet";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CountryPicker, { flagFor } from "@/components/customer/CountryPicker";
import DialerSmartSearch from "@/components/customer/DialerSmartSearch";
import DialerQuickActions from "@/components/customer/DialerQuickActions";
import DialerWalletChip from "@/components/customer/DialerWalletChip";

const DIGITS = [
  ["1", " "], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

// Tiny haptic helper — gracefully ignored on iOS Safari & desktop.
function buzz(ms = 8) {
  try { if (navigator?.vibrate) navigator.vibrate(ms); } catch { /* */ }
}

function formatLocal(num) {
  // Light visual formatting: insert spaces every 3 digits after country code.
  if (!num || !num.startsWith("+")) return num;
  const digits = num.slice(1).replace(/\D/g, "");
  // Heuristic country code length 1-4
  for (const cc of [4, 3, 2, 1]) {
    if (digits.length > cc) {
      const head = digits.slice(0, cc);
      const tail = digits.slice(cc).match(/.{1,3}/g)?.join(" ") || "";
      return `+${head} ${tail}`.trim();
    }
  }
  return num;
}

export default function CallingDialer({ guestPreview = false, size = "md" }) {
  const lg = size === "lg";
  const auth = useCustomerAuth();
  const user = auth?.user || null;
  const isAuthed = !!user && !guestPreview;
  const nav = useNavigate();

  const [countries, setCountries] = useState([]);
  const [picker, setPicker] = useState(false);
  const [country, setCountry] = useState(null);   // { country_code, country_name, prefix, rate_per_minute }
  const [number, setNumber] = useState("");        // local part after the dialing prefix
  const [prefillName, setPrefillName] = useState("");
  const [rate, setRate] = useState(null);
  const [rateErr, setRateErr] = useState("");
  const [wallet, setWallet] = useState(null);
  const [active, setActive] = useState(null);
  const [calling, setCalling] = useState(false);
  const [callSec, setCallSec] = useState(0);
  const [recents, setRecents] = useState([]);
  const [hasHistory, setHasHistory] = useState(false);
  const [smsChannel, setSmsChannel] = useState(null);  // {channel, cost, free, recipient_name}
  const [searchQ, setSearchQ] = useState("");
  const [contacts, setContacts] = useState([]);
  const pollRef = useRef(null);

  // ──────────────────────────── Load reference data ──────────────────────────
  useEffect(() => {
    walletApi.get("/rates/countries")
      .then(({ data }) => {
        setCountries(data.items || []);
        // Default country: account country if callable, else GB, else first.
        const items = data.items || [];
        const accountCC = (user?.country || "").toUpperCase();
        const def =
          items.find((c) => c.country_code === accountCC) ||
          items.find((c) => c.country_code === "GB") ||
          items[0];
        setCountry((cur) => cur || def || null);
      })
      .catch(() => { /* network — non-fatal for guest preview */ });
  }, [user?.country]);

  const refreshWalletAndActive = useCallback(async () => {
    if (!isAuthed) return;
    try {
      const { data } = await walletApi.get("/customer/calls/active");
      setActive(data && (data.call_id || data.id) ? data : null);
    } catch (err) {
      // Active-call poll failure is non-fatal; surface in dev only.
      if (process.env.NODE_ENV !== "production") console.warn("active poll", err?.message);
    }
    try {
      const { data } = await walletApi.get("/customer/wallet/me");
      setWallet(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("wallet poll", err?.message);
    }
  }, [isAuthed]);

  const loadRecents = useCallback(async () => {
    if (!isAuthed) return;
    try {
      const { data } = await walletApi.get("/customer/calls/recent-numbers?limit=5");
      setRecents(data.items || []);
      setHasHistory((data.items || []).length > 0);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("recents", err?.message);
    }
  }, [isAuthed]);

  useEffect(() => { refreshWalletAndActive(); loadRecents(); }, [refreshWalletAndActive, loadRecents]);
  useEffect(() => { if (!active && isAuthed) loadRecents(); }, [active, isAuthed, loadRecents]);

  // Load saved contacts for smart-search & favorites jump.
  useEffect(() => {
    if (!isAuthed) { setContacts([]); return; }
    walletApi.get("/customer/contacts")
      .then(({ data }) => setContacts(data.items || []))
      .catch((err) => { if (process.env.NODE_ENV !== "production") console.warn("contacts", err?.message); });
  }, [isAuthed]);

  // ──────────── Click-to-call prefill from Contacts (sessionStorage) ────────
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("od_dial_prefill");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem("od_dial_prefill");
      if (!parsed?.number || Date.now() - (parsed.ts || 0) > 60_000) return;
      const e164 = String(parsed.number).replace(/[^\d+]/g, "");
      hydrateFromE164(e164);
      if (parsed.name) setPrefillName(parsed.name);
    } catch (err) {
      // Non-fatal: Safari private mode / malformed payload. The dialer just
      // starts blank instead of pre-filling.
      if (process.env.NODE_ENV !== "production") console.warn("dial prefill read", err?.message);
    }
    // `hydrateFromE164` reads `countries` from closure; that's intentional
    // since this effect only runs once on mount and whenever countries first
    // arrive from the API.
  }, [countries.length]);

  function hydrateFromE164(e164) {
    if (!e164 || !e164.startsWith("+")) { setNumber(""); return; }
    const digits = e164.slice(1);
    const match = [...countries].sort((a, b) => b.prefix.length - a.prefix.length)
      .find((c) => digits.startsWith(c.prefix));
    if (match) {
      setCountry(match);
      setNumber(digits.slice(match.prefix.length));
    } else {
      setNumber(digits);
    }
  }

  // ────────────────────── Live rate lookup as user types ────────────────────
  const fullE164 = useMemo(() => {
    if (!country) return "";
    const local = number.replace(/\D/g, "");
    return local ? `+${country.prefix}${local}` : "";
  }, [country, number]);

  // Live SMS channel preview — tells the user whether the message will be free
  // (in-app to another OneDial user) or paid SMS (with cost).
  useEffect(() => {
    if (!isAuthed || !fullE164 || fullE164.length < 6) { setSmsChannel(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await walletApi.get(
          `/customer/sms/channel-lookup?number=${encodeURIComponent(fullE164)}`
        );
        setSmsChannel(data);
      } catch { setSmsChannel(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [isAuthed, fullE164]);

  useEffect(() => {
    if (!fullE164 || fullE164.replace(/\D/g, "").length < 5) {
      setRate(country ? { rate_per_minute: country.rate_per_minute, country_name: country.country_name } : null);
      setRateErr("");
      return;
    }
    const t = setTimeout(async () => {
      setRateErr("");
      try {
        const { data } = await walletApi.get(`/rates/lookup?number=${encodeURIComponent(fullE164)}`);
        setRate(data);
      } catch (err) {
        if (err?.response?.status === 404) setRateErr("No rate available for this destination");
        setRate(country ? { rate_per_minute: country.rate_per_minute, country_name: country.country_name } : null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [fullE164, country]);

  // ───────────────────── Active call polling + ticker ───────────────────────
  useEffect(() => {
    if (active && ["initiated", "ringing", "in_progress"].includes(active.status)) {
      pollRef.current = setInterval(refreshWalletAndActive, 1500);
      return () => clearInterval(pollRef.current);
    }
  }, [active, refreshWalletAndActive]);

  useEffect(() => {
    if (!active || active.status !== "in_progress") { setCallSec(0); return; }
    const started = new Date(active.connected_at || active.created_at).getTime();
    const tick = () => setCallSec(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);

  // ────────────────────────────────── Actions ───────────────────────────────
  function press(d) {
    if (active) return;
    if (prefillName) setPrefillName("");
    if (d === "+") {
      // long-press "0" surrogate: do nothing here, user explicitly tapping +
      return;
    }
    buzz(6);
    setNumber((n) => n + d);
  }
  function back() { buzz(4); setNumber((n) => n.slice(0, -1)); }
  function clearAll() { setNumber(""); setPrefillName(""); setSearchQ(""); }

  // Smart-search pickers.
  function pickContact(c) {
    setSearchQ("");
    hydrateFromE164(c.phone_e164);
    if (c.name) setPrefillName(c.name);
    buzz(10);
  }
  function pickRecent(r) {
    setSearchQ("");
    hydrateFromE164(r.phone_e164);
    if (r.contact_name) setPrefillName(r.contact_name);
    buzz(10);
  }
  function pickCountry(c) {
    setSearchQ("");
    setCountry(c);
    setNumber("");
    buzz(10);
  }

  async function placeCall() {
    if (!fullE164) { toast.error("Enter a number to call"); return; }
    buzz(20);
    if (!isAuthed) {
      // Guest preview — stash the desired number and send them to register.
      try {
        sessionStorage.setItem("od_dial_prefill",
          JSON.stringify({ number: fullE164, ts: Date.now() }));
      } catch (err) {
        // Quota / private-mode failures: degrade gracefully by just
        // redirecting — the user can re-enter the number after sign-up.
        if (process.env.NODE_ENV !== "production") console.warn("dial prefill stash", err?.message);
      }
      nav("/register?next=/app/dial");
      return;
    }
    if (active || calling) return;
    setCalling(true);
    try {
      const { data } = await walletApi.post("/customer/calls/initiate", { to_number: fullE164 });
      toast.success(`Calling ${data.destination}…`);
      setActive({ ...data, status: data.status });
      refreshWalletAndActive();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not place the call";
      toast.error(msg);
      if (msg.toLowerCase().includes("balance")) nav("/app/wallet/topup");
    } finally { setCalling(false); }
  }

  async function endCall() {
    if (!active) return;
    buzz(15);
    try {
      await walletApi.post(`/customer/calls/${active.id || active.call_id}/hangup`);
      toast.success("Call ended");
      setTimeout(refreshWalletAndActive, 800);
    } catch { toast.error("Hangup failed"); }
  }

  // ─────────────────────────────── Derived UI state ─────────────────────────
  const showOnboarding =
    isAuthed && !hasHistory && (wallet?.balance ?? 0) <= 0 && !active;

  const ratePerMin = rate?.rate_per_minute ?? country?.rate_per_minute ?? null;
  const balance = wallet?.balance ?? null;
  const destinationName = rate?.country_name || country?.country_name || "";

  // ────────────────────────────────── Render ────────────────────────────────
  return (
    <div className="w-full max-w-md mx-auto flex flex-col gap-3 min-w-0" data-testid="calling-dialer">
      {/* Wallet snapshot + quick wallet shortcuts (fintech-style) */}
      <DialerWalletChip
        balance={balance}
        rate={ratePerMin}
        destinationName={destinationName}
        isAuthed={isAuthed}
      />

      {/* Smart search — contacts / countries / recents */}
      {isAuthed && (
        <DialerSmartSearch
          value={searchQ}
          onChange={setSearchQ}
          contacts={contacts}
          countries={countries}
          recents={recents}
          onPickContact={pickContact}
          onPickRecent={pickRecent}
          onPickCountry={pickCountry}
        />
      )}

      {/* Country chip + number display */}
      <div className="rounded-2xl bg-white border border-brand-line">
        <button
          type="button" onClick={() => setPicker(true)}
          data-testid="dialer-country-chip"
          className={`w-full flex items-center justify-between gap-3 ${lg ? "px-5 py-4" : "px-4 py-3"} border-b border-brand-line/60 hover:bg-brand-bgSoft transition-colors`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className={`${lg ? "text-3xl" : "text-2xl"} leading-none`} aria-hidden>{flagFor(country?.country_code)}</span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Calling</p>
              <p className={`font-semibold ${lg ? "text-base" : "text-sm"} text-brand-ink truncate`}>
                {country?.country_name || "Choose destination"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`font-mono ${lg ? "text-base" : "text-sm"} font-bold text-brand-greenDark`}>
              {country ? `+${country.prefix}` : "—"}
            </span>
            <ChevronDown className="h-4 w-4 text-brand-muted" />
          </div>
        </button>

        <div className={`${lg ? "px-5 py-5" : "px-4 py-4"}`}>
          {prefillName && !active && (
            <p className="text-center text-xs font-semibold text-brand-greenDark mb-1" data-testid="dialer-prefill-name">
              {prefillName}
            </p>
          )}
          <div className="flex items-center gap-2">
            <div className={lg ? "w-10" : "w-9"} />
            <p className={`flex-1 text-center font-display font-black ${lg ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl sm:text-3xl"} tracking-wider text-brand-ink ${lg ? "min-h-[48px]" : "min-h-[40px]"} break-all`}
               data-testid="dialer-number">
              {number ? formatLocal(`+${country?.prefix || ""}${number}`) : (country ? `+${country.prefix}` : "+")}
            </p>
            <button
              type="button" onClick={back}
              disabled={!number || !!active}
              data-testid="dialer-backspace"
              className={`${lg ? "w-10 h-10" : "w-9 h-9"} inline-flex items-center justify-center rounded-full text-brand-muted hover:bg-brand-bgSoft disabled:opacity-30`}
              aria-label="Backspace"
            >
              <Delete className={lg ? "h-5 w-5" : "h-5 w-5"} />
            </button>
          </div>
          {rateErr && (
            <p className="mt-2 text-[11px] text-rose-600 flex items-center gap-1 justify-center">
              <AlertCircle className="h-3 w-3" /> {rateErr}
            </p>
          )}
        </div>
      </div>

      {/* Active call panel */}
      {active && ["initiated", "ringing", "in_progress"].includes(active.status) && (
        <div className="rounded-2xl bg-brand-greenSoft border border-brand-green p-5 text-center" data-testid="dialer-active">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-greenDark font-bold">
            {active.status === "ringing" ? "Ringing…" : active.status === "in_progress" ? "On call" : "Connecting…"}
          </p>
          <p className="font-display font-black text-2xl mt-2">{active.to_e164 || active.to}</p>
          {active.status === "in_progress" && (
            <p className="text-sm font-semibold text-brand-ink mt-2 inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />{" "}
              {Math.floor(callSec / 60)}:{String(callSec % 60).padStart(2, "0")} ·{" "}
              £{((active.cost_minor || 0) / 100).toFixed(2)}
            </p>
          )}
          <button onClick={endCall} data-testid="dialer-end-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white px-5 py-2 text-sm font-bold">
            <PhoneOff className="h-4 w-4" /> End call
          </button>
        </div>
      )}

      {/* Onboarding strip (zero state) */}
      {showOnboarding && (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-brand-green/30 p-5" data-testid="dialer-onboarding">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-brand-greenDark" />
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-brand-greenDark">Get started in 3 steps</p>
          </div>
          <ol className="space-y-2 mb-4">
            <li className="flex items-center gap-3">
              <span className="h-6 w-6 inline-flex items-center justify-center rounded-full bg-brand-green text-white text-xs font-bold">✓</span>
              <p className="text-sm font-semibold text-brand-ink">Account created</p>
            </li>
            <li className="flex items-center gap-3">
              <span className="h-6 w-6 inline-flex items-center justify-center rounded-full bg-brand-ink text-white text-xs font-bold">2</span>
              <p className="text-sm font-semibold text-brand-ink">Add credit to your wallet</p>
            </li>
            <li className="flex items-center gap-3 opacity-50">
              <span className="h-6 w-6 inline-flex items-center justify-center rounded-full bg-brand-bgSoft text-brand-muted text-xs font-bold">3</span>
              <p className="text-sm font-semibold text-brand-ink">Make your first call</p>
            </li>
          </ol>
          <Link to="/app/wallet/topup" data-testid="dialer-onboarding-cta"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-4 py-3 text-sm font-bold">
            <Wallet className="h-4 w-4" /> Top up to start calling
          </Link>
        </div>
      )}

      {/* Recent calls strip */}
      {!active && isAuthed && recents.length > 0 && (
        <div data-testid="dialer-recents">
          <div className="flex items-center justify-between px-1 mb-1.5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">Recent</p>
            <Link to="/app/calls" className="text-[11px] font-semibold text-brand-greenDark hover:text-brand-greenHover" data-testid="dialer-history-link">
              See all →
            </Link>
          </div>
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 snap-x min-w-0 max-w-full">
            {recents.map((r) => (
              <button key={r.phone_e164} type="button"
                onClick={() => { hydrateFromE164(r.phone_e164); setPrefillName(r.contact_name || ""); }}
                data-testid={`dialer-recent-${r.phone_e164}`}
                className="snap-start shrink-0 rounded-xl border border-brand-line bg-white hover:bg-brand-bgSoft active:scale-95 transition px-3 py-2 text-left min-w-[140px] max-w-[180px]"
              >
                <p className="text-xs font-semibold text-brand-ink truncate">{r.contact_name || r.phone_e164}</p>
                <p className="text-[10px] text-brand-muted font-mono truncate">
                  {r.contact_name ? r.phone_e164 : (r.destination || "—")}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick action row — fintech-style icon bar */}
      {!active && !showOnboarding && (
        <DialerQuickActions
          onCall={() => { if (fullE164) { placeCall(); } else { toast.info("Enter a number to call"); } }}
        />
      )}

      {/* Keypad */}
      {!showOnboarding && (
        <div className={`grid grid-cols-3 ${lg ? "gap-2 sm:gap-3 md:gap-4" : "gap-2 sm:gap-2.5"} w-full`} data-testid="dialer-keypad">
          {DIGITS.map(([d, letters]) => (
            <button
              key={d} type="button" onClick={() => press(d)}
              disabled={!!active}
              data-testid={`dialer-key-${d}`}
              className={`${lg ? "min-h-[68px] sm:min-h-[80px] lg:min-h-[88px]" : "aspect-square sm:aspect-[4/3]"} rounded-2xl bg-white border border-brand-line shadow-[0_2px_0_rgba(7,27,77,0.04)] active:scale-95 active:bg-brand-bgSoft transition disabled:opacity-40 disabled:active:scale-100`}
            >
              <span className={`block font-display font-black ${lg ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"} text-brand-ink`}>{d}</span>
              <span className={`block ${lg ? "text-[11px]" : "text-[10px]"} uppercase tracking-[0.18em] text-brand-muted mt-0.5`}>{letters}</span>
            </button>
          ))}
        </div>
      )}

      {/* Save number to contacts (small) */}
      {isAuthed && !active && number.length >= 4 && (
        <Link to={`/app/contacts?phone=${encodeURIComponent(fullE164)}`}
          data-testid="dialer-save-contact"
          className="self-center inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-muted hover:text-brand-ink">
          <UserPlus className="h-3 w-3" /> Save to contacts
        </Link>
      )}

      {/* Big green call button + Message companion */}
      {!active && !showOnboarding && (
        <div className="mt-1 flex items-stretch gap-2">
          <button
            type="button" onClick={placeCall}
            disabled={!fullE164 || calling || !!rateErr}
            data-testid="dialer-call-btn"
            className={`flex-1 inline-flex items-center justify-center gap-3 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:bg-brand-line disabled:text-brand-muted text-white ${lg ? "py-5 sm:py-6 text-lg" : "py-4 text-base"} font-bold tracking-wide shadow-[0_10px_24px_-10px_rgba(31,139,58,0.7)] transition-colors`}
          >
            {calling ? <Loader2 className={lg ? "h-6 w-6 animate-spin" : "h-5 w-5 animate-spin"} /> : <Phone className={lg ? "h-6 w-6 fill-current" : "h-5 w-5 fill-current"} />}
            {isAuthed ? (calling ? "Connecting…" : "Call") : "Sign up & call"}
          </button>
          {isAuthed && fullE164 && (
            <Link
              to={`/app/messages/${encodeURIComponent(fullE164)}`}
              data-testid="dialer-sms-btn"
              aria-label="Send SMS"
              className={`shrink-0 inline-flex items-center justify-center rounded-full bg-sky-500 hover:bg-sky-600 text-white ${lg ? "px-5" : "px-4"} font-bold transition-colors`}
            >
              <MessageSquare className={lg ? "h-6 w-6" : "h-5 w-5"} />
            </Link>
          )}
        </div>
      )}

      {/* SMS channel pill — shows whether sending will be free (in-app) or paid SMS */}
      {isAuthed && !active && !showOnboarding && smsChannel && (
        <div className="flex justify-center mt-1" data-testid="dialer-sms-channel">
          {smsChannel.channel === "inapp" ? (
            <Link
              to={`/app/messages/${encodeURIComponent(fullE164)}`}
              data-testid="dialer-sms-pill-inapp"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1 text-[11px] font-bold hover:bg-emerald-100"
            >
              <MessageSquare className="h-3 w-3" />
              Message {smsChannel.recipient_name || "OneDial user"} — Free
            </Link>
          ) : (
            <Link
              to={`/app/messages/${encodeURIComponent(fullE164)}`}
              data-testid="dialer-sms-pill-sms"
              className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-800 px-3 py-1 text-[11px] font-bold hover:bg-sky-100"
            >
              <MessageSquare className="h-3 w-3" />
              Send SMS · {gbp(smsChannel.cost)}/msg
            </Link>
          )}
        </div>
      )}

      {/* Quick links to Contacts (logged in only) */}
      {isAuthed && (
        <Link to="/app/contacts" data-testid="dialer-contacts-link"
          className="self-center mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-muted hover:text-brand-ink">
          <Users className="h-3 w-3" /> Pick from Contacts
        </Link>
      )}

      <CountryPicker
        open={picker} onClose={() => setPicker(false)}
        countries={countries} onPick={(c) => { setCountry(c); setNumber(""); }}
        currentCode={country?.country_code}
      />
    </div>
  );
}
